import { OctInputCellGroup, OctLabel, OctTextField } from "@oceantech/oceantech-ui";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import useIndexDBValue from "../../../../hook/useIndexDBValue";
import Autocomplete from "../../../component/AutocompleteObjectV2";
import { IDanhSachDanhMuc } from "../../../models/models";
import { getSimpleCategory } from "../../../utils/AppFunction";
import {
	APP_DATE_FORMAT,
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
	KEY_DS_DANH_MUC_TIEP_DON,
	SIMPLE_CATEGORY_TYPE,
} from "../../../utils/Constant";

type Props = {
	patientInfo: any;
};

const PatientInfo = ({ patientInfo }: Props) => {
	const { value: categories } = useIndexDBValue<IDanhSachDanhMuc>({
		key: KEY_DS_DANH_MUC_TIEP_DON,
	});
	const [provinceId, setProvinceId] = useState("");
	const [wardOptions, setWardOptions] = useState<any[]>([]);

	const formikRef = useRef<FormikProps<any>>(null);

	useEffect(() => {
		if (patientInfo?.id) {
			formikRef.current?.setValues({
				...patientInfo,
				gender: {
					name: patientInfo?.gender,
					value: patientInfo?.gender,
				},
				occupation: convertDataToObject(patientInfo, "occupation"),
				ethnicity: convertDataToObject(patientInfo, "ethnicity"),
				country: convertDataToObject(patientInfo, "country"),
				province: convertDataToObject(patientInfo, "province"),
				commune: convertDataToObject(patientInfo, "commune"),
			});

			if (patientInfo?.provinceCode) {
				setProvinceId(
					categories?.provinces.find((province) => province.code === patientInfo?.provinceCode)?.id
				);
			}
		}
	}, [patientInfo?.id]);

	useEffect(() => {
		if (wardOptions.length && (!provinceId || !categories?.subDistricts?.length)) {
			setWardOptions([]);
			return;
		}

		setWardOptions((categories?.subDistricts ?? []).filter((sub) => sub.provinceId === provinceId));
	}, [provinceId, categories?.subDistricts?.length]);

	const onSubmit = async () => {
		const formValues = formikRef.current?.values;
		console.log(formValues);
	};

	const convertDataToObject = (data: any, field: string) => {
		const fieldCode = `${field}Code`;
		const fieldName = `${field}Name`;

		if (!data?.[fieldCode]) return null;

		return {
			value: data?.[fieldCode],
			name: data?.[fieldName],
		};
	};

	return (
		<div>
			<div className="d-flex align-items-center">
				<h3 className="text-info mb-0 fs-5">Thông tin bệnh nhân</h3>
				<Button className="btn-fill spaces ml-8 px-16" onClick={onSubmit}>
					Cập nhật
				</Button>
			</div>

			<Formik<any> onSubmit={() => {}} initialValues={{}} innerRef={formikRef}>
				{({ values, setFieldValue, touched, errors }) => (
					<Form>
						<Row>
							<Col md={4}>
								<OctLabel label="Mã bệnh nhân" />
								<OctTextField
									name="code"
									value={values?.code}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										setFieldValue(e.target.name, e.target.value);
									}}
								/>
							</Col>
							<Col md={4}>
								<OctLabel label="Họ và tên" />
								<OctTextField
									name="fullName"
									value={values?.personName}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setFieldValue(e.target.name, e.target.value)
									}
									touched={touched?.fullName}
									errors={errors?.fullName}
								/>
							</Col>
							<Col md={2}>
								<OctLabel label="Ngày sinh" />
								<OctInputCellGroup
									cells={[
										{
											name: "dobDay",
											length: 2,
										},
										{
											name: "dobMonth",
											length: 2,
										},
										{
											name: "dobYear",
											length: 4,
										},
									]}
									value={{
										dobDay: values?.dobDay,
										dobMonth: values?.dobMonth,
										dobYear: values?.dobYear,
									}}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										setFieldValue(e.target.name, e.target.value);
									}}
									errors={errors}
									touched={touched}
									inputClassName="customs-input"
								/>
							</Col>
							<Col md={2}>
								<OctLabel label="Giới tính" />
								<Autocomplete
									options={categories?.listGioiTinh || []}
									value={values?.gender}
									name="gender"
									onChange={(selectedOption) => {
										setFieldValue("gender", selectedOption);
									}}
									touched={touched?.gender}
									errors={errors?.gender}
									getOptionValue={option => option?.code}
								/>
							</Col>
						</Row>

						<Row className="spaces mt-8">
							<Col md={4}>
								<OctLabel label="Căn cước" />
								<OctTextField
									className="min-w-130px w-100"
									name="idNumber"
									value={values?.idNumber}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setFieldValue(e.target.name, e.target.value)
									}
									touched={touched?.idNumber}
									error={errors?.idNumber}
								/>
							</Col>
							<Col md={4}>
								<OctLabel label="BHYT" />
								<OctInputCellGroup
									cells={[
										{
											name: "insCodeDoiTuong",
											length: 2,
											className: "text-uppercase",
										},
										{
											name: "insCodeMucHuong",
											length: 1,
										},
										{
											name: "insCodeNoiPhatHanh",
											length: 2,
										},
										{
											name: "insCodeMaBhxh",
											length: 10,
										},
									]}
									value={values}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										setFieldValue(e.target.name, e.target.value);
									}}
									errors={errors?.insurance}
									touched={touched?.insurance}
									inputClassName="customs-input"
								/>
							</Col>
							<Col md={4}>
								<OctLabel label="Dân tộc" />
								<Autocomplete
									options={categories?.listDanToc || []}
									value={values?.ethnicity}
									name="ethnicity"
									onChange={(selectedOption) => setFieldValue("ethnicity", selectedOption)}
									touched={touched?.ethnicity}
									errors={errors?.ethnicity}
									getOptionValue={option => option?.code}
								/>
							</Col>
						</Row>

						<Row className="spaces mt-8">
							<Col>
								<OctLabel label="Nghề nghiệp" />
								<Autocomplete
									options={[]}
									searchFunction={getSimpleCategory}
									searchObject={{
										pageIndex: DEFAULT_PAGE_INDEX,
										pageSize: DEFAULT_PAGE_SIZE,
										type: SIMPLE_CATEGORY_TYPE.NGHE_NGHIEP,
									}}
									isSrcoll
									isSearchable
									labelSearch="keyword"
									value={values?.occupation}
									name="occupation"
									onChange={(selectedOption) => {
										setFieldValue("occupation", selectedOption);
									}}
									touched={touched?.occupation}
									errors={errors?.occupation}
									getOptionValue={option => option?.code}
								/>
							</Col>
							<Col>
								<OctLabel label="Số điện thoại" />
								<OctTextField
									name="phoneNumber"
									value={values?.phoneNumber}
									onChange={(e: ChangeEvent<HTMLInputElement>) => {
										setFieldValue(e.target.name, e.target.value);
									}}
									touched={touched?.phoneNumber}
									errors={errors?.phoneNumber}
								/>
							</Col>
							<Col>
								<OctLabel label="Quốc tịch" />
								<Autocomplete
									options={categories?.listQuocTich || []}
									name="country"
									value={values?.country}
									onChange={(selectedOption) => {
										setFieldValue("country", selectedOption);
									}}
									getOptionValue={option => option?.code}
								/>
							</Col>
						</Row>

						<Row className="spaces mt-8">
							<Col>
								<OctLabel label="Tỉnh/Thành phố" />
								<Autocomplete
									options={categories?.provinces || []}
									value={values?.province}
									name="province"
									onChange={(selectedOption) => {
										setFieldValue("province", selectedOption);
										setProvinceId(selectedOption?.id);
										setFieldValue("commune", null);
									}}
									touched={touched?.province}
									errors={errors?.province}
									getOptionValue={option => option?.code}
								/>
							</Col>
							<Col>
								<OctLabel label="Xã/Phường" />
								<Autocomplete
									options={wardOptions}
									value={values?.commune}
									name="commune"
									onChange={(selectedOption) => {
										setFieldValue("commune", selectedOption);
									}}
									touched={values?.commune}
									errors={errors?.commune}
									dependencies={[provinceId]}
									getOptionValue={option => option?.code}
								/>
							</Col>
							<Col>
								<OctLabel label="Số nhà, đường" />
								<OctTextField
									className="min-w-130px"
									name="address"
									value={values?.address}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setFieldValue(e.target.name, e.target.value)
									}
								/>
							</Col>
						</Row>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default PatientInfo;
